import React, { useState, useEffect } from 'react';
import { useLang } from '../i18n/LangContext';
import SEO from '../components/SEO';
import { captureAttribution } from '../utils/leadTracking';
import Navbar from '../components/sections/Navbar';
import Footer from '../components/sections/Footer';
import { portfolioProjects, ourWorkPhotos, fixFlipProjects } from '../mock';
import { ArrowLeft, X, ChevronLeft, ChevronRight } from 'lucide-react';

const modernMinimalistImages = [
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_e72d340d-3899-4876-891e-4e00170d4616/artifacts/lpyteik4_Imagen%20de%20ChatGPT%2028%20sept%202026%2C%2016_38_37.jpg", label: "Completed Exterior", labelEs: "Exterior Completado" },
  { src: "https://customer-assets.emergentagent.com/job_e72d340d-3899-4876-891e-4e00170d4616/artifacts/6fmzq0r3_Image%205-6-26%20at%207.16%E2%80%AFPM.jpeg", label: "Front Facade", labelEs: "Fachada Frontal" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_e72d340d-3899-4876-891e-4e00170d4616/artifacts/9g0groxj_Imagen%20de%20ChatGPT%2028%20sept%202026%2C%2016_38_43.jpg", label: "Block Construction", labelEs: "Construcción en Bloque" },
  { src: "https://customer-assets.emergentagent.com/job_e72d340d-3899-4876-891e-4e00170d4616/artifacts/fd49nqx1_Image%205-6-26%20at%207.19%E2%80%AFPM.jpeg", label: "Roof Framing", labelEs: "Estructura del Techo" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_e72d340d-3899-4876-891e-4e00170d4616/artifacts/rwhwk4q7_Imagen%20de%20ChatGPT%2028%20sept%202026%2C%2016_38_47.jpg", label: "Interior Framing", labelEs: "Estructura Interior" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_e72d340d-3899-4876-891e-4e00170d4616/artifacts/rtboezad_Imagen%20de%20ChatGPT%2028%20sept%202026%2C%2016_38_51.jpg", label: "Plumbing Rough-In", labelEs: "Instalación de Plomería" },
  { src: "https://customer-assets.emergentagent.com/job_e72d340d-3899-4876-891e-4e00170d4616/artifacts/t69r5oik_Image%205-6-26%20at%207.20%E2%80%AFPM.jpeg", label: "Completed Home", labelEs: "Casa Terminada" },
];

export default function ProjectsPage() {
  const { lang, t } = useLang();
  const tp = t.projects;
  const [lightboxIdx, setLightboxIdx] = useState(null);
  
  const pageTitle = lang === 'es' ? 'Proyectos Completados | Miami Multi-Trade' : 'Completed Projects | Miami Multi-Trade';
  const pageDesc = lang === 'es' ? 'Explore proyectos de renovación, fix & flip y construcción completados por Miami Multi-Trade en el Condado Miami-Dade.' : 'Browse completed renovation, fix & flip, and new construction projects by Miami Multi-Trade in Miami-Dade County.';

  useEffect(() => { captureAttribution(); }, []);

  return (
    <>
      <SEO path="/projects" title={pageTitle} description={pageDesc} />
      <Navbar />
      <main className="bg-[#0B0D0F] pt-24 pb-20 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <a href="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> {tp.back}
          </a>

          <div className="mb-16">
            <span className="text-[#D4AF37] text-xs font-mono tracking-[0.25em] uppercase">{tp.label}</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-3 font-outfit">{tp.heading}</h1>
            <p className="text-slate-400 mt-4 max-w-2xl">{tp.desc}</p>
          </div>

          {/* Portfolio Projects */}
          <section className="mb-20">
            <h2 className="text-2xl font-bold text-white mb-8 font-outfit">{lang === 'es' ? 'Portafolio' : 'Portfolio'}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {portfolioProjects.map(project => (
                <div key={project.id} className="group relative overflow-hidden rounded-lg aspect-[4/3]">
                  <img src={project.image} alt={`${project.title} - ${project.type}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <span className="text-[#D4AF37] text-[10px] font-mono uppercase tracking-wider">{project.type}</span>
                    <h3 className="text-white font-semibold text-sm mt-1 font-outfit">{project.title}</h3>
                    <p className="text-slate-400 text-xs">{project.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Artwork gallery */}
          <section className="mb-20" data-testid="featured-transformations-section">
            <h2 className="text-2xl font-bold text-white mb-8 font-outfit">{lang === 'es' ? 'Nuestros Proyectos' : 'Our Work'}</h2>
            <div className="bg-[#12151A] rounded-xl border border-white/8 overflow-hidden">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                {modernMinimalistImages.map((img, i) => (
                  <div key={i} className={`relative cursor-pointer group overflow-hidden ${i === 0 ? 'sm:col-span-2 sm:row-span-2' : ''}`} onClick={() => setLightboxIdx(i)}>
                    <div className={i === 0 ? "aspect-[16/10]" : "aspect-[4/3]"}>
                      <img src={img.src} alt={lang === 'es' ? img.labelEs : img.label}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                      <span className="text-white/80 text-[10px] font-mono">{lang === 'es' ? img.labelEs : img.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Our Work Photos */}
          <section className="mb-20">
            <h2 className="text-2xl font-bold text-white mb-8 font-outfit">{tp.gallery}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {ourWorkPhotos.map(photo => (
                <div key={photo.id} className="group relative overflow-hidden rounded-lg aspect-square">
                  <img src={photo.image} alt={photo.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-end">
                    <p className="text-white text-xs p-3 opacity-0 group-hover:opacity-100 transition-opacity font-outfit">{photo.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Completed Projects (real photos) — only visible here via View All */}
          <section className="mb-20" data-testid="completed-real-projects">
            <h2 className="text-2xl font-bold text-white mb-8 font-outfit">{lang === 'es' ? 'Proyectos Completados' : 'Completed Projects'}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { t: 'Renovated Single-Family Home', te: 'Casa Unifamiliar Renovada', img: 'https://customer-assets.emergentagent.com/job_e72d340d-3899-4876-891e-4e00170d4616/artifacts/6fmzq0r3_Image%205-6-26%20at%207.16%E2%80%AFPM.jpeg', type: 'Completed Renovation', typeEs: 'Renovación Completada', loc: 'Miami, FL' },
                { t: 'Complete Property Renovation', te: 'Transformación Completa de Propiedad', img: 'https://customer-assets.emergentagent.com/job_e72d340d-3899-4876-891e-4e00170d4616/artifacts/t69r5oik_Image%205-6-26%20at%207.20%E2%80%AFPM.jpeg', type: 'Fix & Flip', typeEs: 'Fix & Flip', loc: 'Miami, FL' },
                { t: 'Redlands Miami Project', te: 'Proyecto Redlands Miami', img: 'https://customer-assets.emergentagent.com/job_e72d340d-3899-4876-891e-4e00170d4616/artifacts/fd49nqx1_Image%205-6-26%20at%207.19%E2%80%AFPM.jpeg', type: 'Completed Project', typeEs: 'Proyecto Completado', loc: 'Redlands, Miami, FL' },
              ].map((p, i) => (
                <div key={i} className="group relative overflow-hidden rounded-lg aspect-[4/3]">
                  <img src={p.img} alt={`${lang === 'es' ? p.te : p.t} - ${p.loc}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <span className="text-[#D4AF37] text-[10px] font-mono uppercase tracking-wider">{lang === 'es' ? p.typeEs : p.type}</span>
                    <h3 className="text-white font-semibold text-sm mt-1 font-outfit">{lang === 'es' ? p.te : p.t}</h3>
                    <p className="text-slate-400 text-xs">{p.loc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Fix & Flip Section — shown last, consolidated under one title */}
          {fixFlipProjects.length > 0 && (
            <section data-testid="fix-flip-section">
              <h2 className="text-2xl font-bold text-white mb-8 font-outfit">{tp.fixFlip}</h2>
              <div className="bg-[#12151A] rounded-lg border border-white/8 overflow-hidden p-6">
                <h3 className="text-xl font-semibold text-white mb-6 font-outfit">{lang === 'es' ? 'Transformación Completa del Hogar' : 'Complete Home Transformation'}</h3>
                <div className="space-y-8">
                  {fixFlipProjects.map(project => (
                    <div key={project.id}>
                      {project.beforeImage === project.afterImage ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="rounded-lg overflow-hidden">
                            <img src={project.beforeImage} alt={project.title} className="w-full h-auto object-contain" loading="lazy" />
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="rounded-lg overflow-hidden">
                            <img src={project.beforeImage} alt={project.title} className="w-full h-auto object-contain" loading="lazy" />
                          </div>
                          <div className="rounded-lg overflow-hidden">
                            <img src={project.afterImage} alt={project.title} className="w-full h-auto object-contain" loading="lazy" />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-slate-400 mt-6 text-center text-sm">{lang === 'es' ? 'Propiedades deterioradas transformadas en hogares modernos de primera calidad — de principio a fin.' : 'Distressed properties transformed into premium modern homes — from start to finish.'}</p>
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={() => setLightboxIdx(null)}>
          <button onClick={() => setLightboxIdx(null)} className="absolute top-4 right-4 text-white/70 hover:text-white z-10">
            <X className="w-8 h-8" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx - 1 + modernMinimalistImages.length) % modernMinimalistImages.length); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-10 bg-black/40 rounded-full p-2">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setLightboxIdx((lightboxIdx + 1) % modernMinimalistImages.length); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-10 bg-black/40 rounded-full p-2">
            <ChevronRight className="w-6 h-6" />
          </button>
          <div className="max-w-5xl max-h-[85vh] px-4" onClick={(e) => e.stopPropagation()}>
            <img src={modernMinimalistImages[lightboxIdx].src} alt={lang === 'es' ? modernMinimalistImages[lightboxIdx].labelEs : modernMinimalistImages[lightboxIdx].label}
              className="max-w-full max-h-[80vh] object-contain rounded-lg" />
            <p className="text-center text-white/70 text-sm mt-3 font-mono">
              {lang === 'es' ? modernMinimalistImages[lightboxIdx].labelEs : modernMinimalistImages[lightboxIdx].label} — {lightboxIdx + 1}/{modernMinimalistImages.length}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
